import React from 'react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Bot, Settings, Check, Lock, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

export type AssessmentType = 'ai-assisted' | 'customized';

interface AssessmentTypeStepProps {
  onSelect: (type: AssessmentType) => void;
  selectedType: AssessmentType | null;
  isBasicPlan?: boolean;
}

const AssessmentTypeStep: React.FC<AssessmentTypeStepProps> = ({ 
  onSelect, 
  selectedType,
  isBasicPlan = false 
}) => {
  return (
    <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-10 backdrop-blur-sm">
      <h1 className="text-3xl font-bold text-center mb-3 text-[#1A1F2C]">Assessment Type</h1>
      
      <p className="text-center text-[#1A1F2C]/80 mb-8">
        Choose how you want to create your assessment
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        {/* AI Assisted Option */}
        <motion.div 
          whileHover={{ scale: 1.02 }}
          className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
            selectedType === 'ai-assisted' 
              ? 'border-[#9b87f5] bg-[#9b87f5]/10' 
              : 'border-gray-200 hover:border-[#9b87f5]/50'
          }`}
          onClick={() => onSelect('ai-assisted')}
        >
          <div className="flex justify-between items-start">
            <div className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] w-12 h-12 rounded-full flex items-center justify-center text-white mb-4">
              <Bot size={24} />
            </div>
            {selectedType === 'ai-assisted' && (
              <div className="bg-[#9b87f5] rounded-full p-1">
                <Check className="h-4 w-4 text-white" />
              </div>
            )}
          </div>
          <h3 className="text-xl font-semibold mb-2 text-[#1A1F2C]">AI Assisted</h3>
          <p className="text-[#1A1F2C]/70 text-sm mb-4">
            Our AI analyzes the role requirements and generates an optimized assessment with 
            tailored questions based on the skills needed for the role.
          </p>
          <ul className="text-sm text-[#1A1F2C]/80 space-y-2">
            <li className="flex items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#9b87f5] mr-2"></div>
              Automated question generation
            </li>
            <li className="flex items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#9b87f5] mr-2"></div>
              Industry standard evaluation criteria
            </li>
            <li className="flex items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#9b87f5] mr-2"></div>
              Quick setup process
            </li>
          </ul>
        </motion.div>
        
        {/* Customized Option */}
        <motion.div 
          whileHover={!isBasicPlan ? { scale: 1.02 } : {}}
          className={`border-2 rounded-lg p-6 ${
            isBasicPlan 
              ? 'cursor-not-allowed border-gray-200 bg-gray-50' 
              : selectedType === 'customized'
                ? 'cursor-pointer border-[#9b87f5] bg-[#9b87f5]/10'
                : 'cursor-pointer border-gray-200 hover:border-[#9b87f5]/50'
          }`}
          onClick={() => !isBasicPlan && onSelect('customized')}
        >
          <div className="flex justify-between items-start">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white mb-4 ${
              isBasicPlan 
                ? 'bg-gray-400'
                : 'bg-gradient-to-r from-[#9b87f5] to-[#7662DA]'
            }`}>
              {isBasicPlan ? <Lock size={24} /> : <Settings size={24} />}
            </div>
            {selectedType === 'customized' && !isBasicPlan && (
              <div className="bg-[#9b87f5] rounded-full p-1">
                <Check className="h-4 w-4 text-white" />
              </div>
            )}
            {isBasicPlan && (
              <Badge className="bg-orange-100 text-orange-800 border border-orange-300">
                Premium Feature
              </Badge>
            )}
          </div>
          
          <h3 className="text-xl font-semibold mb-2 text-[#1A1F2C] flex items-center gap-2">
            Customized
            {isBasicPlan && <Lock className="h-4 w-4 text-gray-400" />}
          </h3>
          
          <p className={`text-sm mb-4 ${isBasicPlan ? 'text-[#1A1F2C]/50' : 'text-[#1A1F2C]/70'}`}>
            Take complete control of your assessment by defining your own questions, 
            selecting the language and accent of the AI interviewer.
          </p>
          
          <ul className={`text-sm space-y-2 ${isBasicPlan ? 'text-[#1A1F2C]/50' : 'text-[#1A1F2C]/80'}`}>
            <li className="flex items-center">
              <div className={`w-1.5 h-1.5 rounded-full mr-2 ${isBasicPlan ? 'bg-gray-400' : 'bg-[#9b87f5]'}`}></div>
              Create your own questions
            </li>
            <li className="flex items-center">
              <div className={`w-1.5 h-1.5 rounded-full mr-2 ${isBasicPlan ? 'bg-gray-400' : 'bg-[#9b87f5]'}`}></div>
              Choose AI interviewer language & accent
            </li>
            <li className="flex items-center">
              <div className={`w-1.5 h-1.5 rounded-full mr-2 ${isBasicPlan ? 'bg-gray-400' : 'bg-[#9b87f5]'}`}></div>
              Full customization options
            </li>
          </ul>
          
          {isBasicPlan && (
            <div className="mt-4 bg-orange-50 border border-orange-200 rounded-md p-3 text-orange-800 text-xs">
              <p>This feature requires a Premium or Agency subscription.</p>
              <Link 
                to="/credits" 
                className="flex items-center gap-1 mt-2 text-[#7662DA] font-medium hover:underline"
              >
                <ExternalLink size={14} />
                Upgrade your plan now
              </Link>
            </div>
          )}
        </motion.div>
      </div>
      
      <div className="flex justify-center">
        <Button 
          variant="purple"
          className="px-8 py-6 bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] shadow-md"
          onClick={() => {
            if (selectedType) {
              onSelect(selectedType);
            }
          }}
          disabled={!selectedType}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default AssessmentTypeStep; 